import { alpha, Box, Checkbox, Toolbar, Typography } from "@mui/material";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import React, { ChangeEvent } from "react";
import { useLanguage } from "../../Language";

interface SelectionToolBarProps {
  isLoading?: boolean;
  totalHits?: number;
  items: ReadonlyArray<{ id: number }>;
  selected?: ReadonlyArray<number>;
  showSelectAll?: boolean;
  handleSelectAllChange?: (
    //  ()=>void MUI requires an {event, checked}() but I can imagine in some other scenarios where it would be preferable to use a different callback
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  unSelectedFragment?: React.ReactNode;
  selectedFragment: React.ReactNode;
  textFragment?: React.ReactNode;
}

function SelectionToolBar({
  isLoading,
  totalHits,
  items,
  selected,
  showSelectAll,
  handleSelectAllChange,
  unSelectedFragment,
  selectedFragment,
  textFragment,
}: SelectionToolBarProps) {
  const isAllSelected = items.length > 0 && selected?.length === items.length;
  const isAllIndeterminate =
    selected?.length > 0 && selected?.length < items.length;
  const selectedLength = selected?.length;

  const isMobile = useIsMobile();
  const { translate } = useLanguage();

  const showCheckbox = showSelectAll || selected?.length > 0;

  return (
    <>
      <Toolbar
        sx={{
          flexWrap: "wrap",
          flexDirection: isMobile ? "row-reverse" : "row",
          alignItems: "center",
          gap: 1,
          pl: { sm: 0, xs: 0 },
          pr: { xs: 0, sm: 1 },
          ...(selectedLength > 0 && {
            bgcolor: (theme) =>
              alpha(
                theme.palette.primary.main,
                theme.palette.action.activatedOpacity,
              ),
          }),
        }}
      >
        {
          <Box sx={{ visibility: showCheckbox ? "visible" : "hidden" }}>
            <Checkbox
              color="primary"
              disabled={isLoading || totalHits === 0}
              indeterminate={isAllIndeterminate}
              checked={isAllSelected}
              onChange={handleSelectAllChange}
              slotProps={{ input: { "aria-label": translate("selectAll") } }}
            />
          </Box>
        }
        {/* unselected */}
        {selectedLength === 0 && <>{unSelectedFragment}</>}
        {/* Selected */}
        {selectedLength > 0 && (
          <>
            {textFragment ? (
              <>{textFragment}</>
            ) : (
              <Typography
                sx={{ flex: "1 1", textWrap: "nowrap" }}
                color="inherit"
                variant="subtitle1"
                component="div"
              >
                {selectedLength} {translate("selected")}
              </Typography>
            )}
            <Box flex={1} />
            {selectedFragment}
          </>
        )}
      </Toolbar>
    </>
  );
}

export default SelectionToolBar;
